import React from 'react';
import { supabase } from '../../../../lib/supabase';
import type { Plant } from '../../../../types/plant';
import * as Icons from 'lucide-react';
import { Loader2 } from 'lucide-react';

interface FavoriteUse {
  id: string;
  title: string;
  description: string;
  icon_name: string;
}

interface FavoriteUsesProps {
  plant: Plant;
}

export default function FavoriteUses({ plant }: FavoriteUsesProps) {
  const [uses, setUses] = React.useState<FavoriteUse[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    async function fetchUses() {
      if (!plant?.id) return;

      try {
        const { data, error } = await supabase
          .from('favorite_uses')
          .select('*')
          .eq('plant_id', plant.id);

        if (error) throw error;
        setUses(data || []);
      } catch (err) {
        console.error('Error fetching favorite uses:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    }

    fetchUses();
  }, [plant?.id]); // Only re-run when plant.id changes

  if (!plant?.id || loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600">
        Error loading favorite uses: {error}
      </div>
    );
  }

  if (uses.length === 0) {
    return null;
  }

  return (
    <section className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-2xl font-semibold mb-6">Our Favorite Uses</h2>
      <div className="space-y-4">
        {uses.map((use) => {
          // Dynamically get the icon component
          const Icon = Icons[use.icon_name as keyof typeof Icons] || Icons.Bookmark;
          
          return (
            <div key={use.id} className="flex gap-3 items-start">
              <Icon className="text-primary-500 flex-shrink-0" />
              <div>
                <h3 className="font-medium">{use.title}</h3>
                <p className="text-gray-600">{use.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}